import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import adminApi from '../../lib/adminApi';
import { toast } from 'sonner';
import { GestorePageHeader } from '../../components/gestore/GestorePageHeader';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { UserPlus, Trash2, KeyRound, Building2, ShieldCheck, UserCog } from 'lucide-react';

export default function GestoreManagers() {
  const { t } = useTranslation();
  const [managers, setManagers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ username: '', name: '', password: '' });
  const [createOpen, setCreateOpen] = useState(false);
  const [pwd, setPwd] = useState({ open: false, mid: null, value: '' });
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, r] = await Promise.all([
        adminApi.get('/admin/managers'),
        adminApi.get('/admin/restaurants'),
      ]);
      setManagers(m.data || []);
      setRestaurants(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gm.loadError', { defaultValue: 'Errore di caricamento' }));
    } finally { setLoading(false); }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const createManager = async () => {
    if (!form.username.trim()) { toast.error(t('gm.usernameRequired', { defaultValue: 'Username obbligatorio' })); return; }
    setCreating(true);
    try {
      await adminApi.post('/admin/managers', {
        username: form.username.trim(), name: form.name.trim(), password: form.password.trim() || undefined,
      });
      toast.success(t('gm.created', { defaultValue: 'Gestore creato' }));
      setForm({ username: '', name: '', password: '' });
      setCreateOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gm.createError', { defaultValue: 'Errore creazione' }));
    } finally { setCreating(false); }
  };

  const resetPassword = async () => {
    if ((pwd.value || '').trim().length < 6) { toast.error(t('gm.pwdShort', { defaultValue: 'Min 6 caratteri' })); return; }
    try {
      await adminApi.post(`/admin/managers/${pwd.mid}/reset-password`, { password: pwd.value.trim() });
      toast.success(t('gm.pwdReset', { defaultValue: 'Password aggiornata' }));
      setPwd({ open: false, mid: null, value: '' });
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gm.error', { defaultValue: 'Errore' }));
    }
  };

  const deleteManager = async (m) => {
    try {
      await adminApi.delete(`/admin/managers/${m.id}`);
      toast.success(t('gm.deleted', { defaultValue: 'Gestore eliminato' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gm.error', { defaultValue: 'Errore' }));
    } finally { setConfirmDel(null); }
  };

  const assign = async (rid, managerId) => {
    try {
      await adminApi.put(`/admin/restaurants/${rid}/assign-manager`, { manager_id: managerId === 'none' ? null : managerId });
      setRestaurants((prev) => prev.map((r) => r.id === rid ? {
        ...r,
        manager_id: managerId === 'none' ? null : managerId,
        manager_name: managerId === 'none' ? null : (managers.find((m) => m.id === managerId)?.name),
      } : r));
      toast.success(t('gm.assigned', { defaultValue: 'Assegnazione aggiornata' }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('gm.error', { defaultValue: 'Errore' }));
    }
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-4 md:py-6" data-testid="gestore-managers-page">
      <GestorePageHeader
        title={t('gm.title', { defaultValue: 'Gestori' })}
        subtitle={t('gm.subtitle', { defaultValue: 'Crea i gestori e assegna loro i locali. Ogni gestore vedrà solo i locali assegnati.' })}
      >
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-xl" data-testid="gm-create-open"><UserPlus className="h-4 w-4 mr-2" /> {t('gm.newManager', { defaultValue: 'Nuovo gestore' })}</Button>
          </DialogTrigger>
          <DialogContent data-testid="gm-create-dialog">
            <DialogHeader><DialogTitle>{t('gm.newManager', { defaultValue: 'Nuovo gestore' })}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>{t('gm.username', { defaultValue: 'Username' })}</Label>
                <Input value={form.username} onChange={(e) => setForm((s) => ({ ...s, username: e.target.value }))} placeholder="mario.rossi" className="mt-1.5" data-testid="gm-username" />
              </div>
              <div>
                <Label>{t('gm.name', { defaultValue: 'Nome' })}</Label>
                <Input value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} placeholder="Mario Rossi" className="mt-1.5" data-testid="gm-name" />
              </div>
              <div>
                <Label>{t('gm.password', { defaultValue: 'Password' })}</Label>
                <Input value={form.password} onChange={(e) => setForm((s) => ({ ...s, password: e.target.value }))} placeholder={t('gm.pwdDefault', { defaultValue: 'Predefinita: gestore123' })} className="mt-1.5" data-testid="gm-password" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={createManager} disabled={creating} data-testid="gm-create-submit">{t('gm.create', { defaultValue: 'Crea gestore' })}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </GestorePageHeader>

      {/* Managers list */}
      <Card className="p-5 mb-5">
        <div className="font-semibold flex items-center gap-2 mb-3"><UserCog className="h-4 w-4" /> {t('gm.managersList', { defaultValue: 'Gestori' })}</div>
        {loading ? (
          <div className="text-sm text-muted-foreground py-6 text-center">{t('common.loading', { defaultValue: 'Caricamento…' })}</div>
        ) : managers.length === 0 ? (
          <div className="text-sm text-muted-foreground py-6 text-center" data-testid="gm-empty">{t('gm.noManagers', { defaultValue: 'Nessun gestore creato. Crea il primo gestore per assegnargli dei locali.' })}</div>
        ) : (
          <div className="space-y-2">
            {managers.map((m) => (
              <div key={m.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`gm-manager-${m.id}`}>
                <div className="min-w-0 flex items-center gap-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0"><ShieldCheck className="h-5 w-5" /></span>
                  <div className="min-w-0">
                    <div className="font-semibold text-sm truncate">{m.name || m.username}</div>
                    <div className="text-xs text-muted-foreground truncate">@{m.username} · {m.restaurants_count} {t('gm.locali', { defaultValue: 'locali' })}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => setPwd({ open: true, mid: m.id, value: '' })} data-testid={`gm-reset-${m.id}`}><KeyRound className="h-4 w-4 mr-1" /> {t('gm.resetPwd', { defaultValue: 'Password' })}</Button>
                  <Button variant="ghost" size="sm" className="text-destructive" onClick={() => setConfirmDel(m)} data-testid={`gm-delete-${m.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Restaurant assignment */}
      <Card className="p-5">
        <div className="font-semibold flex items-center gap-2 mb-1"><Building2 className="h-4 w-4" /> {t('gm.assignTitle', { defaultValue: 'Assegnazione locali' })}</div>
        <div className="text-xs text-muted-foreground mb-4">{t('gm.assignHint', { defaultValue: 'Scegli quale gestore gestisce ciascun locale.' })}</div>
        <div className="space-y-2">
          {restaurants.map((r) => (
            <div key={r.id} className="flex items-center justify-between gap-3 rounded-xl border border-border p-3" data-testid={`gm-restaurant-${r.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm truncate">{r.name}</div>
                <div className="text-xs text-muted-foreground truncate">{r.manager_name ? `${t('gm.manager', { defaultValue: 'Gestore' })}: ${r.manager_name}` : t('gm.noManager', { defaultValue: 'Nessun gestore assegnato' })}</div>
              </div>
              <Select value={r.manager_id || 'none'} onValueChange={(v) => assign(r.id, v)}>
                <SelectTrigger className="w-52 shrink-0" data-testid={`gm-assign-${r.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('gm.none', { defaultValue: 'Nessuno' })}</SelectItem>
                  {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.name || m.username}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </Card>

      {/* Reset password dialog */}
      <Dialog open={pwd.open} onOpenChange={(o) => setPwd((s) => ({ ...s, open: o }))}>
        <DialogContent data-testid="gm-pwd-dialog">
          <DialogHeader><DialogTitle>{t('gm.resetPwdTitle', { defaultValue: 'Nuova password gestore' })}</DialogTitle></DialogHeader>
          <Input type="text" value={pwd.value} onChange={(e) => setPwd((s) => ({ ...s, value: e.target.value }))} placeholder={t('gm.newPwd', { defaultValue: 'Nuova password (min 6)' })} data-testid="gm-pwd-input" />
          <DialogFooter><Button onClick={resetPassword} data-testid="gm-pwd-submit">{t('gm.save', { defaultValue: 'Salva' })}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('gm.deleteTitle', { defaultValue: 'Eliminare il gestore?' })}</AlertDialogTitle>
            <AlertDialogDescription>{t('gm.deleteDesc', { defaultValue: 'I locali assegnati resteranno senza gestore. Questa azione è irreversibile.' })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="gm-del-cancel">{t('gm.cancel', { defaultValue: 'Annulla' })}</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleteManager(confirmDel)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="gm-del-confirm">{t('gm.delete', { defaultValue: 'Elimina' })}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
